import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  onSnapshot,
  WhereFilterOp,
  Unsubscribe,
  FirestoreDataConverter,
  WriteBatch,
  Transaction
} from 'firebase/firestore';
import type { Project } from '@segmently/schema-models/entities';
import { CoreFirebaseService } from './CoreFirebaseService';
import { TaskService } from './TaskService';
import { projectConverter as clientProjectConverter } from '../converters/projectConverter';

/**
 * ProjectService
 *
 * Type-safe service for Project entity CRUD operations and real-time subscriptions.
 *
 * Features:
 * - Full CRUD operations (create, read, update, delete, list)
 * - Query operations with filtering
 * - Real-time subscriptions with automatic cleanup
 * - Type-safe: only Project types exposed, no Firebase types in public API
 * - Subcollection access via getTasksService()
 * - Uses schema-generated projectConverter for Date↔Timestamp conversion
 *
 * Usage:
 * ```typescript
 * import { ProjectService } from '@/modules/firebase-core/services/ProjectService';
 * import { projectConverter } from '@schema/mappers/projectConverter';
 *
 * const service = new ProjectService(projectConverter);
 *
 * // Create
 * const project = await service.create({ name: 'My Project' });
 *
 * // Read
 * const found = await service.get(project.id!);
 *
 * // Update
 * const updated = await service.update(project.id!, { name: 'Updated' });
 *
 * // Delete
 * await service.delete(project.id!);
 *
 * // List with pagination
 * const projects = await service.list({ limit: 10 });
 *
 * // Query
 * const webProjects = await service.query('platform', '==', 'web');
 *
 * // Real-time subscription
 * const unsubscribe = service.onSnapshot(
 *   project.id!,
 *   (p) => console.log('Updated:', p),
 *   (err) => console.error(err)
 * );
 * // Later: unsubscribe();
 *
 * // Access tasks subcollection
 * const taskService = service.getTasksService(project.id!);
 * ```
 */
export class ProjectService {
  private readonly collectionName = 'projects';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<Project>;

  /**
   * Create a new ProjectService instance
   *
   * @param converter - Firestore converter for Project entity (defaults to client SDK converter)
   */
  constructor(converter: FirestoreDataConverter<Project> = clientProjectConverter) {
    this.converter = converter;
  }

  /**
   * Create a new project
   *
   * @param data - Partial project data (id will be auto-generated if not provided)
   * @returns {Promise<Project>} The created project with generated ID
   *
   * @example
   * ```typescript
   * const project = await service.create({
   *   name: 'My Project',
   *   description: 'A great project',
   *   platform: 'web'
   * });
   * console.log(project.id); // Auto-generated ID
   * ```
   */
  async create(data: Partial<Project>): Promise<Project> {
    try {
      const collectionRef = collection(this.firestore, this.collectionName);
      const docRef = doc(collectionRef).withConverter(this.converter);

      const projectData: Project = {
        ...data,
        id: docRef.id,
        creationDate: new Date(),
        updatedAt: new Date(),
      } as Project;

      await setDoc(docRef, projectData);

      return projectData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.create');
      throw error;
    }
  }

  /**
   * Create project and initialize owner membership (two-step pattern)
   *
   * CRITICAL: This is the CORRECT way to create projects.
   * Always use this method instead of create() directly.
   *
   * This method ensures:
   * 1. Project document is created
   * 2. Owner member document is created in subcollection
   * 3. memberIds array is initialized
   *
   * Without these steps, Firestore Security Rules will block project access.
   *
   * @param data - Project data
   * @param ownerId - User ID of the project owner
   * @param ownerEmail - Email of the project owner
   * @returns {Promise<Project>} Created project with owner membership
   *
   * @example
   * ```typescript
   * const service = new ProjectService();
   * const project = await service.createWithOwner(
   *   { name: 'My Project', platform: 'web' },
   *   'user-123',
   *   'owner@example.com'
   * );
   * ```
   */
  async createWithOwner(
    data: Partial<Project>,
    ownerId: string,
    ownerEmail: string
  ): Promise<Project> {
    try {
      // Step 1: Create project document
      const project = await this.create(data);

      if (!project.id) {
        throw new Error('Project created without ID');
      }

      // Step 2: Create owner member document
      const memberRef = doc(
        this.firestore,
        `projects/${project.id}/members/${ownerId}`
      );

      await setDoc(memberRef, {
        role: 'owner',
        email: ownerEmail,
        joinedAt: new Date(),
      });

      // Step 3: Initialize memberIds array
      const projectRef = doc(this.firestore, 'projects', project.id);
      await updateDoc(projectRef, {
        memberIds: [ownerId],
      });

      console.log(`✓ Project ${project.id} created with owner ${ownerId}`);

      return project;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(
        error,
        'ProjectService.createWithOwner'
      );
      throw error;
    }
  }

  /**
   * Get a project by ID
   *
   * @param id - Project ID
   * @returns {Promise<Project | null>} The project or null if not found
   *
   * @example
   * ```typescript
   * const project = await service.get('project-123');
   * if (project) {
   *   console.log(project.name);
   * }
   * ```
   */
  async get(id: string): Promise<Project | null> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id).withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as Project;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.get');
      throw error;
    }
  }

  /**
   * Update an existing project
   *
   * @param id - Project ID
   * @param data - Partial project data to update
   * @returns {Promise<Project>} The updated project
   *
   * @example
   * ```typescript
   * const updated = await service.update('project-123', {
   *   name: 'Updated Name',
   *   description: 'New description'
   * });
   * ```
   */
  async update(id: string, data: Partial<Project>): Promise<Project> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id).withConverter(this.converter);

      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      // Use setDoc with merge to go through converter
      await setDoc(docRef, updateData as Project, { merge: true });

      // Fetch and return updated document
      const updated = await this.get(id);
      if (!updated) {
        throw new Error(`Project ${id} not found after update`);
      }

      return updated;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.update');
      throw error;
    }
  }

  /**
   * Delete a project
   *
   * @param id - Project ID
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await service.delete('project-123');
   * ```
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.delete');
      throw error;
    }
  }

  /**
   * List projects with optional pagination
   *
   * @param options - Query options
   * @param options.limit - Maximum number of projects to return
   * @param options.startAfter - Start after this value (for pagination)
   * @returns {Promise<Project[]>} Array of projects
   *
   * @example
   * ```typescript
   * // First page
   * const firstPage = await service.list({ limit: 10 });
   *
   * // Next page
   * const lastProject = firstPage[firstPage.length - 1];
   * const nextPage = await service.list({
   *   limit: 10,
   *   startAfter: lastProject.creationDate
   * });
   * ```
   */
  async list(options: { limit?: number; startAfter?: any } = {}): Promise<Project[]> {
    try {
      const collectionRef = collection(this.firestore, this.collectionName).withConverter(this.converter);

      let q = query(collectionRef, orderBy('creationDate', 'desc'));

      if (options.limit) {
        q = query(q, limit(options.limit));
      }

      if (options.startAfter) {
        q = query(q, startAfter(options.startAfter));
      }

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Project));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.list');
      throw error;
    }
  }

  /**
   * Query projects by field
   *
   * @param field - Field name to query
   * @param operator - Comparison operator
   * @param value - Value to compare against
   * @returns {Promise<Project[]>} Array of matching projects
   *
   * @example
   * ```typescript
   * // Find all web platform projects
   * const webProjects = await service.query('platform', '==', 'web');
   *
   * // Find projects created after a date
   * const recentProjects = await service.query(
   *   'creationDate',
   *   '>',
   *   '2025-01-01'
   * );
   * ```
   */
  async query(field: string, operator: WhereFilterOp, value: any): Promise<Project[]> {
    try {
      const collectionRef = collection(this.firestore, this.collectionName).withConverter(this.converter);
      const q = query(collectionRef, where(field, operator, value));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Project));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.query');
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for a project
   *
   * @param id - Project ID
   * @param onNext - Callback when project is updated (receives Project | null)
   * @param onError - Callback when an error occurs
   * @returns {Unsubscribe} Function to call to unsubscribe
   *
   * @example
   * ```typescript
   * const unsubscribe = service.onSnapshot(
   *   'project-123',
   *   (project) => {
   *     if (project) {
   *       console.log('Project updated:', project.name);
   *     } else {
   *       console.log('Project deleted');
   *     }
   *   },
   *   (error) => console.error('Subscription error:', error)
   * );
   *
   * // Later: cleanup subscription
   * unsubscribe();
   * ```
   */
  onSnapshot(
    id: string,
    onNext: (project: Project | null) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    try {
      const docRef = doc(this.firestore, this.collectionName, id).withConverter(this.converter);

      return onSnapshot(
        docRef,
        (snapshot) => {
          if (snapshot.exists()) {
            // Get data through converter
            const data = snapshot.data();
            // Add id field
            const project: Project = { ...data, id: snapshot.id };
            onNext(project);
          } else {
            onNext(null);
          }
        },
        (error) => {
          CoreFirebaseService.getInstance().logError(error, 'ProjectService.onSnapshot');
          onError(error as Error);
        }
      );
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.onSnapshot.setup');
      throw error;
    }
  }

  /**
   * Update a project in a batch operation
   *
   * @param batch - Firestore WriteBatch instance
   * @param id - Project ID
   * @param data - Partial project data to update
   *
   * @example
   * ```typescript
   * const batch = core.batch();
   * service.batchUpdate(batch, 'project-1', { name: 'Updated 1' });
   * service.batchUpdate(batch, 'project-2', { name: 'Updated 2' });
   * await batch.commit();
   * ```
   */
  batchUpdate(batch: WriteBatch, id: string, data: Partial<Project>): void {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);

      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      // Use converter to transform TypeScript entity → Firestore data
      // Converts ISO strings → Timestamps
      const firestoreData = this.converter.toFirestore(updateData as Project);

      // Use batch.update() to update ONLY specified fields (not set with merge)
      batch.update(docRef, firestoreData);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.batchUpdate');
      throw error;
    }
  }

  /**
   * Delete a project in a batch operation
   *
   * @param batch - Firestore WriteBatch instance
   * @param id - Project ID
   *
   * @example
   * ```typescript
   * const batch = core.batch();
   * service.batchDelete(batch, 'project-1');
   * service.batchDelete(batch, 'project-2');
   * await batch.commit();
   * ```
   */
  batchDelete(batch: WriteBatch, id: string): void {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);
      batch.delete(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.batchDelete');
      throw error;
    }
  }

  /**
   * Get a project within a transaction
   *
   * @param transaction - Firestore Transaction instance
   * @param id - Project ID
   * @returns {Promise<Project | null>} The project or null if not found
   *
   * @example
   * ```typescript
   * await core.runTransaction(async (txn) => {
   *   const project = await service.transactionGet(txn, 'project-123');
   *   if (project) {
   *     await service.transactionUpdate(txn, project.id!, {
   *       counter: (project.counter || 0) + 1
   *     });
   *   }
   * });
   * ```
   */
  async transactionGet(transaction: Transaction, id: string): Promise<Project | null> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);
      const docSnap = await transaction.get(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      // Use converter to transform Firestore data → TypeScript entity
      // Converts Timestamps → ISO strings
      const project = this.converter.fromFirestore(docSnap as any, {});
      return { ...project, id: docSnap.id } as Project;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.transactionGet');
      throw error;
    }
  }

  /**
   * Update a project within a transaction
   *
   * @param transaction - Firestore Transaction instance
   * @param id - Project ID
   * @param data - Partial project data to update
   *
   * @example
   * ```typescript
   * await core.runTransaction(async (txn) => {
   *   const project = await service.transactionGet(txn, 'project-123');
   *   await service.transactionUpdate(txn, 'project-123', {
   *     counter: (project?.counter || 0) + 1
   *   });
   * });
   * ```
   */
  async transactionUpdate(transaction: Transaction, id: string, data: Partial<Project>): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);

      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      // Use converter to transform TypeScript entity → Firestore data
      // Converts ISO strings → Timestamps
      const firestoreData = this.converter.toFirestore(updateData as Project);

      // Use transaction.update() to update ONLY specified fields (not set with merge)
      transaction.update(docRef, firestoreData);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectService.transactionUpdate');
      throw error;
    }
  }

  /**
   * Get TaskService for a project's tasks subcollection
   *
   * @param projectId - Project ID
   * @returns {TaskService} TaskService instance scoped to this project
   *
   * @example
   * ```typescript
   * const taskService = service.getTasksService('project-123');
   *
   * // Now use taskService for tasks in this project
   * const task = await taskService.create({
   *   name: 'My Task',
   *   description: 'Task description'
   * });
   * ```
   */
  getTasksService(projectId: string): TaskService {
    // Import client SDK taskConverter (Sprint 5 - T012.1C)
    const { taskConverter } = require('../converters/taskConverter');
    return new TaskService(projectId, taskConverter);
  }
}

// ============================================================================
// TEST UTILITIES
// ============================================================================

/**
 * Create a mock ProjectService for testing
 *
 * Usage in tests:
 * ```typescript
 * import { createMockProjectService } from '@modules/firebase-core/services/ProjectService';
 *
 * const mockService = createMockProjectService({
 *   get: jest.fn().mockResolvedValue(mockProject),
 * });
 * ```
 *
 * @param overrides - Optional method overrides
 * @returns Mocked ProjectService instance
 */
export function createMockProjectService(
  overrides: Partial<jest.Mocked<ProjectService>> = {}
): jest.Mocked<ProjectService> {
  return {
    // CRUD operations
    list: jest.fn().mockResolvedValue([]),
    get: jest.fn().mockResolvedValue(null),
    create: jest.fn().mockResolvedValue({ id: 'new-project-id' }),
    update: jest.fn().mockResolvedValue({ id: 'updated-project-id' }),
    delete: jest.fn().mockResolvedValue(undefined),
    query: jest.fn().mockResolvedValue([]),
    onSnapshot: jest.fn().mockReturnValue(() => {}),

    // Batch operations
    batchUpdate: jest.fn(),
    batchDelete: jest.fn(),

    // Transaction operations
    transactionGet: jest.fn().mockResolvedValue(null),
    transactionUpdate: jest.fn().mockResolvedValue(undefined),

    // Subcollection access
    getAudiencesService: jest.fn().mockReturnValue({}),
    getActorsService: jest.fn().mockReturnValue({}),
    getTasksService: jest.fn().mockReturnValue({}),

    // Allow overrides
    ...overrides,
  } as unknown as jest.Mocked<ProjectService>;
}
